import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../services/api';
import { BACKEND_URL } from '../config';

const BusinessProducts = ({ projectMode = false }) => {
  // Contexte du projet si en mode projet
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  const canManage = projectMode ? outletContext?.canManage : true;
  
  // Base URL de l'API selon le mode
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showModal, setShowModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    nom: '', description: '', code_produit: '', categorie: '',
    prix_achat: '', prix_vente: '', stock_actuel: 0, stock_minimum: 5, unite: 'unité', image: '', is_published: true
  });
  const [stockAction, setStockAction] = useState({ type: 'entree', quantite: '', motif: '' });
  const [importData, setImportData] = useState({ products: [], updateExisting: false });
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, projectId]);

  const loadProducts = async () => {
    try {
      let url = `${apiBase}/products`;
      if (filter === 'low_stock') url += '?stock_bas=true';
      else if (filter === 'inactive') url += '?actif=false';
      
      const res = await api.get(url);
      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedProduct) {
        await api.put(`${apiBase}/products/${selectedProduct.id}`, formData);
      } else {
        await api.post(`${apiBase}/products`, formData);
      }
      loadProducts();
      closeModal();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const handleStockSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`${apiBase}/products/${selectedProduct.id}/stock`, stockAction);
      loadProducts();
      setShowStockModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await api.delete(`${apiBase}/products/${id}`);
      loadProducts();
    } catch (err) {
      console.error(err);
    }
  };

  // =====================
  // UPLOAD IMAGE
  // =====================

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier le type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Format non supporté. Utilisez JPG, PNG, GIF ou WebP.');
      return;
    }

    // Vérifier la taille (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image trop volumineuse (max 5MB)');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/upload_product.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      const result = await response.json();
      
      if (result.success) {
        setFormData({ ...formData, image: result.url });
      } else {
        alert(result.error || 'Erreur lors de l\'upload');
      }
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, image: '' });
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${BACKEND_URL}/${imagePath}`;
  };

  // =====================
  // IMPORT / EXPORT
  // =====================

  const handleExport = async () => {
    try {
      const res = await api.get(`${apiBase}/products/export`);
      const csvData = res.data.data;
      
      // Convertir en CSV avec point-virgule (format Excel français)
      const csvContent = csvData.map(row => 
        row.map(cell => {
          const str = String(cell ?? '');
          // Échapper les guillemets et entourer si nécessaire
          if (str.includes(';') || str.includes('"') || str.includes('\n')) {
            return '"' + str.replace(/"/g, '""') + '"';
          }
          return str;
        }).join(';')
      ).join('\n');
      
      // Créer et télécharger le fichier CSV
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `produits_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'export');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const parsedProducts = parseCSV(text);
        setImportData({ ...importData, products: parsedProducts });
        setImportResult(null);
      } catch (err) {
        alert('Erreur lors de la lecture du fichier: ' + err.message);
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    if (lines.length < 2) throw new Error('Le fichier doit contenir au moins un en-tête et une ligne de données');

    // Parser l'en-tête
    const headerLine = lines[0];
    const headers = parseCSVLine(headerLine).map(h => h.toLowerCase().trim());
    
    // Mapping des colonnes
    const columnMap = {
      nom: findColumn(headers, ['nom', 'name', 'produit', 'product', 'designation', 'désignation', 'libelle', 'libellé']),
      code_produit: findColumn(headers, ['code', 'code_produit', 'code produit', 'reference', 'référence', 'ref', 'sku']),
      categorie: findColumn(headers, ['categorie', 'catégorie', 'category', 'famille', 'type']),
      prix_achat: findColumn(headers, ['prix_achat', 'prix achat', 'cout', 'coût', 'purchase_price', 'achat']),
      prix_vente: findColumn(headers, ['prix_vente', 'prix vente', 'prix', 'price', 'vente', 'pv', 'selling_price']),
      stock_actuel: findColumn(headers, ['stock', 'stock_actuel', 'stock actuel', 'quantite', 'quantité', 'qty', 'quantity']),
      stock_minimum: findColumn(headers, ['stock_minimum', 'stock minimum', 'stock_min', 'min', 'seuil']),
      unite: findColumn(headers, ['unite', 'unité', 'unit', 'uom']),
      description: findColumn(headers, ['description', 'desc', 'details', 'détails', 'note', 'notes']),
      image: findColumn(headers, ['image', 'photo', 'picture', 'img', 'url_image', 'image_url'])
    };

    if (columnMap.nom === -1) {
      throw new Error('Colonne "Nom" non trouvée. Assurez-vous que votre fichier contient une colonne avec un de ces noms: Nom, Name, Produit, Designation');
    }

    // Parser les lignes de données
    const products = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length === 0 || values.every(v => !v.trim())) continue;

      const product = {
        nom: columnMap.nom >= 0 ? values[columnMap.nom]?.trim() || '' : '',
        code_produit: columnMap.code_produit >= 0 ? values[columnMap.code_produit]?.trim() || '' : '',
        categorie: columnMap.categorie >= 0 ? values[columnMap.categorie]?.trim() || '' : '',
        prix_achat: columnMap.prix_achat >= 0 ? parseNumber(values[columnMap.prix_achat]) : 0,
        prix_vente: columnMap.prix_vente >= 0 ? parseNumber(values[columnMap.prix_vente]) : 0,
        stock_actuel: columnMap.stock_actuel >= 0 ? parseInt(values[columnMap.stock_actuel]) || 0 : 0,
        stock_minimum: columnMap.stock_minimum >= 0 ? parseInt(values[columnMap.stock_minimum]) || 5 : 5,
        unite: columnMap.unite >= 0 ? values[columnMap.unite]?.trim() || 'unité' : 'unité',
        description: columnMap.description >= 0 ? values[columnMap.description]?.trim() || '' : '',
        image: columnMap.image >= 0 ? values[columnMap.image]?.trim() || '' : ''
      };

      if (product.nom) {
        products.push(product);
      }
    }

    return products;
  };

  const parseCSVLine = (line) => {
    // Détecter le séparateur (tabulation, virgule ou point-virgule)
    const tabCount = (line.match(/\t/g) || []).length;
    const commaCount = (line.match(/,/g) || []).length;
    const semicolonCount = (line.match(/;/g) || []).length;
    
    // Utiliser le séparateur le plus fréquent
    let separator = '\t';
    if (commaCount > tabCount && commaCount > semicolonCount) separator = ',';
    else if (semicolonCount > tabCount && semicolonCount > commaCount) separator = ';';
    
    // Si tabulation, split simple
    if (separator === '\t') {
      return line.split('\t').map(cell => cell.trim());
    }
    
    // Sinon, parser avec gestion des guillemets
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (inQuotes) {
        if (char === '"' && line[i + 1] === '"') {
          current += '"';
          i++;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          current += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === separator) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
    }
    result.push(current.trim());
    return result;
  };

  const findColumn = (headers, possibleNames) => {
    for (const name of possibleNames) {
      const index = headers.findIndex(h => h === name || h.includes(name));
      if (index >= 0) return index;
    }
    return -1;
  };

  const parseNumber = (value) => {
    if (!value) return 0;
    // Supprimer les espaces et remplacer la virgule par un point
    const cleaned = String(value).replace(/\s/g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  };

  const handleImport = async () => {
    if (importData.products.length === 0) return;
    
    setImporting(true);
    try {
      const res = await api.post(`${apiBase}/products/import`, {
        products: importData.products,
        update_existing: importData.updateExisting
      });
      
      setImportResult(res.data);
      
      if (res.data.imported > 0 || res.data.updated > 0) {
        loadProducts();
      }
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportData({ products: [], updateExisting: false });
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const downloadTemplate = () => {
    // Format CSV avec point-virgule (compatible Excel français)
    const template = 'Nom;Code Produit;Catégorie;Prix Achat;Prix Vente;Stock;Stock Minimum;Unité;Description;Image\nProduit exemple;PRD001;Electronique;5000;7500;10;5;unité;Description du produit;';
    
    const blob = new Blob(['\ufeff' + template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'modele_import_produits.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const openModal = (product = null) => {
    if (product) {
      setSelectedProduct(product);
      setFormData({
        nom: product.nom || '',
        description: product.description || '',
        code_produit: product.code_produit || '',
        categorie: product.categorie || '',
        prix_achat: product.prix_achat || '',
        prix_vente: product.prix_vente || '',
        stock_actuel: product.stock_actuel || 0,
        stock_minimum: product.stock_minimum || 5,
        unite: product.unite || 'unité',
        image: product.image || '',
        is_published: product.is_published === 1 || product.is_published === true
      });
    } else {
      setSelectedProduct(null);
      setFormData({
        nom: '', description: '', code_produit: '', categorie: '',
        prix_achat: '', prix_vente: '', stock_actuel: 0, stock_minimum: 5, unite: 'unité', image: '', is_published: true
      });
    }
    if (imageInputRef.current) imageInputRef.current.value = '';
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const openStockModal = (product) => {
    setSelectedProduct(product);
    setStockAction({ type: 'entree', quantite: '', motif: '' });
    setShowStockModal(true);
  };

  const filteredProducts = products.filter(p =>
    p.nom?.toLowerCase().includes(search.toLowerCase()) ||
    p.code_produit?.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Produits & Stock</h1>
          <p className="text-gray-500">Gérez votre inventaire</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManage && !projectMode && (
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition flex items-center gap-2"
            >
              <span>📥</span> Importer
            </button>
          )}
          {!projectMode && (
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition flex items-center gap-2"
            >
              <span>📤</span> Exporter
            </button>
          )}
          {canManage && (
            <button
              onClick={() => openModal()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center gap-2"
            >
              <span>➕</span> Nouveau produit
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-400 focus:outline-none"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {[
              { value: 'all', label: 'Tous' },
              { value: 'low_stock', label: '⚠️ Stock bas' },
              { value: 'inactive', label: 'Inactifs' }
            ].map(f => (
              <button
                key={f.value}
                onClick={() => { setFilter(f.value); setCurrentPage(1); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  filter === f.value
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Résultat de recherche */}
      {search && (
        <div className="mb-4 text-sm text-gray-500">
          {filteredProducts.length} résultat(s) pour "{search}"
        </div>
      )}

      {/* Products Grid */}
      {paginatedProducts.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {paginatedProducts.map(product => (
            <div key={product.id} className="bg-white rounded-xl shadow hover:shadow-md transition overflow-hidden">
              {/* Image du produit */}
              {product.image ? (
                <div className="h-40 bg-gray-100 overflow-hidden">
                  <img 
                    src={getImageUrl(product.image)} 
                    alt={product.nom}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="h-40 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                  <span className="text-5xl opacity-50">📦</span>
                </div>
              )}
              
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-800 truncate">{product.nom}</h3>
                    {product.code_produit && (
                      <p className="text-xs text-gray-400">#{product.code_produit}</p>
                    )}
                  </div>
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    product.stock_actuel <= product.stock_minimum
                      ? 'bg-red-100 text-red-600'
                      : 'bg-green-100 text-green-600'
                  }`}>
                    Stock: {product.stock_actuel}
                  </span>
                </div>

                {/* Badges catégorie + statut boutique */}
                <div className="flex flex-wrap gap-1 mb-2">
                  {product.categorie && (
                    <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      {product.categorie}
                    </span>
                  )}
                  {/* Indicateur de publication sur la boutique */}
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${
                    product.is_published === 1 || product.is_published === true
                      ? 'bg-indigo-100 text-indigo-600'
                      : 'bg-gray-100 text-gray-400'
                  }`}>
                    {product.is_published === 1 || product.is_published === true ? (
                      <>🌐 En ligne</>
                    ) : (
                      <>🚫 Hors ligne</>
                    )}
                  </span>
                </div>

                <div className="flex justify-between items-center text-sm mb-3">
                  <div>
                    <span className="text-gray-500">Achat:</span>
                    <span className="ml-1 font-medium">{product.prix_achat?.toLocaleString()} XOF</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Vente:</span>
                    <span className="ml-1 font-bold text-indigo-600">{product.prix_vente?.toLocaleString()} XOF</span>
                  </div>
                </div>

                <div className="flex gap-2 pt-3 border-t">
                  {canManage && (
                    <button
                      onClick={() => openStockModal(product)}
                      className="flex-1 py-2 bg-emerald-50 text-emerald-600 rounded-lg text-sm hover:bg-emerald-100 transition"
                    >
                      📦 Stock
                    </button>
                  )}
                  {canManage && (
                    <button
                      onClick={() => openModal(product)}
                      className="flex-1 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-sm hover:bg-indigo-100 transition"
                    >
                      ✏️ Modifier
                    </button>
                  )}
                  {canManage && (
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="py-2 px-3 bg-red-50 text-red-600 rounded-lg text-sm hover:bg-red-100 transition"
                    >
                      🗑️
                    </button>
                  )}
                  {!canManage && (
                    <span className="text-gray-400 text-xs py-2">Lecture seule</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <span className="text-6xl mb-4 block">📦</span>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {search ? 'Aucun résultat' : 'Aucun produit'}
          </h3>
          <p className="text-gray-500 mb-4">
            {search ? `Aucun produit ne correspond à "${search}"` : 'Commencez par ajouter vos produits'}
          </p>
          {!search && (
            <button
              onClick={() => openModal()}
              className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
            >
              Ajouter un produit
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className={`px-3 py-2 rounded-lg ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
          >
            ««
          </button>
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-lg ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
          >
            ← Précédent
          </button>
          
          <div className="flex gap-1 items-center">
            {(() => {
              const pages = [];
              const showPages = new Set();
              
              showPages.add(1);
              showPages.add(totalPages);
              for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
                showPages.add(i);
              }
              
              const sortedPages = [...showPages].sort((a, b) => a - b);
              
              sortedPages.forEach((page, idx) => {
                if (idx > 0 && page - sortedPages[idx - 1] > 1) {
                  pages.push(
                    <span key={`ellipsis-${page}`} className="px-2 text-gray-400">...</span>
                  );
                }
                pages.push(
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-10 h-10 rounded-lg ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
                  >
                    {page}
                  </button>
                );
              });
              
              return pages;
            })()}
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-lg ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
          >
            Suivant →
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className={`px-3 py-2 rounded-lg ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 shadow'}`}
          >
            »»
          </button>
        </div>
      )}

      {/* Modal Produit */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-800">
                {selectedProduct ? 'Modifier le produit' : 'Nouveau produit'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du produit *</label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code produit</label>
                  <input
                    type="text"
                    value={formData.code_produit}
                    onChange={(e) => setFormData({...formData, code_produit: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                  <input
                    type="text"
                    value={formData.categorie}
                    onChange={(e) => setFormData({...formData, categorie: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={2}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prix d'achat (XOF)</label>
                  <input
                    type="number"
                    value={formData.prix_achat}
                    onChange={(e) => setFormData({...formData, prix_achat: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prix de vente (XOF) *</label>
                  <input
                    type="number"
                    value={formData.prix_vente}
                    onChange={(e) => setFormData({...formData, prix_vente: e.target.value})}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                </div>
              </div>

              {/* Stock initial uniquement à la création */}
              {!selectedProduct && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stock initial</label>
                  <input
                    type="number"
                    value={formData.stock_actuel}
                    onChange={(e) => setFormData({...formData, stock_actuel: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                </div>
              )}

              {/* Seuil et unité - toujours visibles */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Seuil d'alerte (stock minimum)</label>
                  <input
                    type="number"
                    value={formData.stock_minimum}
                    onChange={(e) => setFormData({...formData, stock_minimum: parseInt(e.target.value) || 5})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                    placeholder="5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unité</label>
                  <input
                    type="text"
                    value={formData.unite}
                    onChange={(e) => setFormData({...formData, unite: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                    placeholder="unité, kg, L..."
                  />
                </div>
              </div>

              {/* Image du produit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Image du produit</label>
                {formData.image ? (
                  <div className="relative inline-block">
                    <img 
                      src={getImageUrl(formData.image)} 
                      alt="Produit" 
                      className="w-32 h-32 object-cover rounded-xl border"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-indigo-400 transition">
                    <input
                      type="file"
                      ref={imageInputRef}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="product-image"
                    />
                    <label htmlFor="product-image" className="cursor-pointer">
                      {uploading ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-indigo-600 border-t-transparent"></div>
                          <span className="text-gray-500">Upload en cours...</span>
                        </div>
                      ) : (
                        <>
                          <span className="text-3xl block mb-1">📷</span>
                          <p className="text-sm text-gray-600">Cliquez pour ajouter une image</p>
                          <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP (max 5MB)</p>
                        </>
                      )}
                    </label>
                  </div>
                )}
              </div>

              {/* Publication sur la boutique */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <div className="font-medium text-gray-800">Publier sur la boutique</div>
                  <div className="text-sm text-gray-500">Ce produit sera visible dans votre boutique en ligne</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_published}
                    onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition disabled:opacity-50"
                >
                  {selectedProduct ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Stock */}
      {showStockModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-800">Ajuster le stock</h2>
              <p className="text-gray-500 text-sm">{selectedProduct.nom}</p>
              <p className="text-lg font-semibold mt-2">Stock actuel: {selectedProduct.stock_actuel} {selectedProduct.unite}</p>
            </div>
            <form onSubmit={handleStockSubmit} className="p-6 space-y-4">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setStockAction({...stockAction, type: 'entree'})}
                  className={`flex-1 py-3 rounded-xl font-medium transition ${
                    stockAction.type === 'entree'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  ➕ Entrée
                </button>
                <button
                  type="button"
                  onClick={() => setStockAction({...stockAction, type: 'sortie'})}
                  className={`flex-1 py-3 rounded-xl font-medium transition ${
                    stockAction.type === 'sortie'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  ➖ Sortie
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantité</label>
                <input
                  type="number"
                  min="1"
                  value={stockAction.quantite}
                  onChange={(e) => setStockAction({...stockAction, quantite: e.target.value})}
                  required
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none text-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motif (optionnel)</label>
                <input
                  type="text"
                  value={stockAction.motif}
                  onChange={(e) => setStockAction({...stockAction, motif: e.target.value})}
                  placeholder="Ex: Achat fournisseur, Vente comptoir..."
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <span className="text-gray-500">Nouveau stock: </span>
                <span className="font-bold text-lg">
                  {stockAction.type === 'entree'
                    ? selectedProduct.stock_actuel + (parseInt(stockAction.quantite) || 0)
                    : Math.max(0, selectedProduct.stock_actuel - (parseInt(stockAction.quantite) || 0))
                  } {selectedProduct.unite}
                </span>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowStockModal(false)}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className={`flex-1 py-2 text-white rounded-xl transition ${
                    stockAction.type === 'entree'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  Confirmer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Import */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-800">📥 Importer des produits</h2>
                <button onClick={closeImportModal} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
              </div>
              <p className="text-gray-500 text-sm mt-1">Importez vos produits depuis un fichier Excel ou CSV</p>
            </div>

            <div className="p-6 space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h3 className="font-semibold text-blue-800 mb-2">💡 Comment ça marche ?</h3>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Préparez votre fichier Excel/CSV avec vos produits</li>
                  <li>Assurez-vous d'avoir au minimum une colonne <strong>"Nom"</strong></li>
                  <li>Sélectionnez votre fichier ci-dessous</li>
                  <li>Vérifiez l'aperçu et lancez l'import</li>
                </ol>
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-sm text-blue-600 mb-2">Colonnes reconnues automatiquement :</p>
                  <div className="flex flex-wrap gap-1">
                    {['Nom', 'Code', 'Catégorie', 'Prix Achat', 'Prix Vente', 'Stock', 'Unité', 'Description'].map(col => (
                      <span key={col} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{col}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Télécharger modèle */}
              <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
                <div>
                  <p className="font-medium text-gray-800">Besoin d'un modèle ?</p>
                  <p className="text-sm text-gray-500">Téléchargez notre fichier exemple</p>
                </div>
                <button
                  onClick={downloadTemplate}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition flex items-center gap-2"
                >
                  📋 Télécharger modèle
                </button>
              </div>

              {/* Sélection fichier */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sélectionner votre fichier</label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-indigo-400 transition">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv,.txt,.xls,.xlsx"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="import-file"
                  />
                  <label htmlFor="import-file" className="cursor-pointer">
                    <span className="text-4xl block mb-2">📂</span>
                    <p className="text-gray-600">Cliquez pour sélectionner un fichier</p>
                    <p className="text-sm text-gray-400 mt-1">CSV, Excel (.csv, .xls, .xlsx)</p>
                  </label>
                </div>
              </div>

              {/* Aperçu des données */}
              {importData.products.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-green-800">
                      ✅ {importData.products.length} produit(s) détecté(s)
                    </h3>
                    <button
                      onClick={() => {
                        setImportData({ ...importData, products: [] });
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Annuler
                    </button>
                  </div>
                  
                  {/* Aperçu tableau */}
                  <div className="bg-white rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Nom</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Code</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Catégorie</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Prix</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Stock</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {importData.products.slice(0, 5).map((p, i) => (
                          <tr key={i}>
                            <td className="px-3 py-2 font-medium text-gray-800">{p.nom}</td>
                            <td className="px-3 py-2 text-gray-600">{p.code_produit || '-'}</td>
                            <td className="px-3 py-2 text-gray-600">{p.categorie || '-'}</td>
                            <td className="px-3 py-2 text-right text-gray-800">{p.prix_vente?.toLocaleString()} XOF</td>
                            <td className="px-3 py-2 text-right text-gray-600">{p.stock_actuel}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {importData.products.length > 5 && (
                      <div className="text-center py-2 bg-gray-50 text-sm text-gray-500">
                        ... et {importData.products.length - 5} autre(s) produit(s)
                      </div>
                    )}
                  </div>

                  {/* Option mise à jour */}
                  <label className="flex items-center gap-2 mt-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={importData.updateExisting}
                      onChange={(e) => setImportData({ ...importData, updateExisting: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">
                      Mettre à jour les produits existants (même nom ou code)
                    </span>
                  </label>
                </div>
              )}

              {/* Résultat import */}
              {importResult && (
                <div className={`rounded-xl p-4 ${importResult.errors?.length > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                  <h3 className="font-semibold mb-2">📊 Résultat de l'import</h3>
                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{importResult.imported}</div>
                      <div className="text-xs text-gray-500">Créé(s)</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{importResult.updated}</div>
                      <div className="text-xs text-gray-500">Mis à jour</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">{importResult.errors?.length || 0}</div>
                      <div className="text-xs text-gray-500">Erreur(s)</div>
                    </div>
                  </div>
                  {importResult.errors?.length > 0 && (
                    <div className="bg-white rounded-lg p-3 max-h-24 overflow-y-auto">
                      {importResult.errors.map((err, i) => (
                        <p key={i} className="text-sm text-red-600">{err}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-6 border-t bg-gray-50 flex gap-3">
              <button
                onClick={closeImportModal}
                className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition"
              >
                {importResult ? 'Fermer' : 'Annuler'}
              </button>
              {!importResult && (
                <button
                  onClick={handleImport}
                  disabled={importData.products.length === 0 || importing}
                  className={`flex-1 py-3 rounded-xl transition flex items-center justify-center gap-2 ${
                    importData.products.length > 0 && !importing
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {importing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      Import en cours...
                    </>
                  ) : (
                    <>
                      📥 Importer {importData.products.length} produit(s)
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessProducts;
